import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Accountbankinfo } from '@core/models/admin/account';
import { JointAccountbankinfo } from '@core/models/admin/account/joint';
import { CommonService, BankListDatum } from '@core/services/common.service';
import { IndividualRegistrationService } from '@core/services/individual-registration.service';
import { LoadingService } from '@core/services/loading.service';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import {
  ButtonDirective,
  SpinDirective,
} from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import { TabsComponent } from '@shared/ui/tabs/tabs.component';
import { BankRegion, CURRENCY } from 'app/app.constants';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-edit-bank-details',
  standalone: true,
  imports: [
    KycAccordionComponent,
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    InputLabelComponent,
    ButtonDirective,
    BaseSelectDirective,
    SpinDirective,
    TabsComponent,
  ],
  templateUrl: './edit-bank-details.component.html',
  styleUrl: './edit-bank-details.component.scss',
})
export class EditBankDetailsComponent implements OnInit, OnDestroy {
  @Input() bankDetails!: Accountbankinfo[] | JointAccountbankinfo[];

  bankForm!: FormGroup;
  bankAccountNo!: string;
  // isFetchingAccountNo: boolean = false;
  banks: BankListDatum[] = [];
  isUpdating$!: Observable<boolean>;
  isAddingNewBankInfo$!: Observable<boolean>;
  isBankListLoading$!: Observable<boolean>;
  isBankValidationLoading$!: Observable<boolean>;
  subs: Subscription[] = [];
  bankForms: FormGroup[] = []; // Array to hold multiple bank forms
  // accountId!: string;
  currencyList = CURRENCY;

  isBankCreatedSubject$ = new BehaviorSubject<boolean>(false);
  accountIdSubject$ = new BehaviorSubject<string | null>(null);
  hasBankNameSubject$ = new BehaviorSubject<boolean>(false);
  bankIdSubject$ = new BehaviorSubject<string | null>(null);
  private bankRegionSubject$ = new BehaviorSubject<BankRegion>('local');

  hasBankName$ = this.hasBankNameSubject$.asObservable();
  bankRegion$ = this.bankRegionSubject$.asObservable();
  isBankCreated$ = this.isBankCreatedSubject$.asObservable();

  fb = inject(FormBuilder);
  commonService = inject(CommonService);
  cdr = inject(ChangeDetectorRef);
  toast = inject(NotificationFacade);
  private route = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private individualService = inject(IndividualRegistrationService);

  constructor() {
    this.isUpdating$ = this.loadingService.getLoadingObservable(
      'update-banking-details'
    );
    this.isAddingNewBankInfo$ = this.loadingService.getLoadingObservable(
      'add-banking-details-to-account'
    );

    this.isBankListLoading$ =
      this.loadingService.getLoadingObservable('v2/get-bank-list');
    this.isBankValidationLoading$ = this.loadingService.getLoadingObservable(
      'validate-bank-details'
    );
  }

  ngOnInit(): void {
    const accountId = this.route.snapshot.parent?.params['accountId'];
    if (accountId) {
      this.accountIdSubject$.next(accountId);
      this.getListOfBanks();
      this.initializeBankForms();
    }
  }

  // Initialize forms based on existing bank details or create default
  initializeBankForms() {
    if (this.bankDetails && this.bankDetails.length > 0) {
      // Create forms for each existing bank detail
      this.bankDetails.forEach((detail) => {
        this.addBankForm(detail);
      });
      this.isBankCreatedSubject$.next(true);
    } else {
      // Add one default form
      this.addBankForm();
    }
  }

  // Add new bank form (with optional existing data)
  addBankForm(existingData?: any) {
    const newForm = this.fb.group({
      id: [existingData?.id || ''],
      bank_account_name: [existingData?.bank_account_name || ''],
      bank_account_number: [
        existingData?.bank_account_number || '',
        Validators.required,
      ],
      bank_code: [existingData?.bank_code || '', Validators.required],
      bank_branch: [existingData?.bank_branch || ''],
      bank_sort_code: [existingData?.bank_sort_code || ''],
      currency: [existingData?.currency || '', Validators.required],
      account_id: [this.accountIdSubject$.value],
      bank_name: [existingData?.bank_name || ''], // for foreign accounts
      region: [existingData?.region || 'local'],
    });

    this.bankForms.push(newForm);
  }

  // Remove bank form
  removeBankForm(index: number) {
    if (this.bankForms.length > 1) {
      const removedItems = this.bankForms.splice(index, 1);
      const removedId = removedItems[0]?.get('id')?.value;
      this.individualService.deleteBankDetails({ id: removedId }).subscribe({
        next: () => {
          this.toast.success('Successfully removed bank details');
        },
        error: () => {
          this.toast.error('Failed to remove bank details');
        },
      });
    } else {
      this.toast.info('You need at least one bank account');
    }
  }

  // Submit all bank forms
  submitAllBankDetails() {
    console.log(this.bankForms);
    const allValid = this.bankForms.every((form) => form.valid);
    if (!allValid) {
      this.toast.error('Please fill all required fields in all bank forms');
      return;
    }

    const requests = this.bankForms.map((form) => {
      const formData = form.getRawValue();

      // Remove bank_name for local accounts and bank_code for foreign accounts
      if (formData.region === 'foreign') {
        delete formData.bank_code;
      }

      if (formData.id) {
        // For updating existing bank details
        return this.individualService.updateBankDetails(formData);
      } else {
        // For adding new bank details
        delete formData.id; // Remove empty id field for new records
        return this.individualService.addBankDetails(formData);
      }
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.toast.success('All bank details saved successfully');
        this.isBankCreatedSubject$.next(true);
      },
      error: () => {
        this.toast.error('Some bank details failed to save');
      },
    });
  }

  updateBankDetails() {
    if (this.bankRegionSubject$.value === 'foreign') {
      this.bankForm.removeControl('bank_code');
    }
    this.bankForm?.addControl(
      'id',
      new FormControl(this.accountIdSubject$.value)
    );

    const sub = this.individualService
      .updateBankDetails(this.bankForm.getRawValue())
      .subscribe({
        next: () => {
          this.toast.success('Successfully updated bank details');
        },
        error: () => {
          this.toast.error('Failed to update bank details');
        },
      });

    this.subs.push(sub);
  }

  createNewBankDetails() {
    // Remove id from the form if it exists
    this.bankForm.removeControl('id');
    console.log(this.bankForm.getRawValue());
    const sub = this.individualService
      .addBankDetails(this.bankForm.getRawValue())
      .subscribe({
        next: (response) => {
          this.isBankCreatedSubject$.next(true);
          this.bankIdSubject$.next(response.data[0].id);
          this.toast.success('Successfully added new bank details');
        },
        error: () => {
          this.toast.error('Failed to add new bank details');
        },
      });
    this.subs.push(sub);
  }

  setBankRegion(region: BankRegion) {
    this.bankRegionSubject$.next(region);
  }

  getListOfBanks() {
    const sub = this.commonService.getBankList().subscribe({
      next: (res: any) => {
        this.banks = res.data;
      },
      error: () => {
        this.toast.info('Unable to fetch bank list.\nKindly refresh');
      },
    });
    this.subs.push(sub);
  }

  // Update toggleBankRegion to work with specific form
  toggleBankRegion(event: Event, bankType: BankRegion, index: number) {
    event.preventDefault();
    const form = this.bankForms[index];

    form.patchValue({
      region: bankType,
    });

    if (bankType === 'local') {
      form.removeControl('bank_name');
      if (!form.contains('bank_code')) {
        form.addControl('bank_code', new FormControl('', Validators.required));
      }
    } else {
      form.addControl('bank_name', new FormControl(''));
      form.removeControl('bank_code');
    }
  }

  // Update validateBankAccountNo to work with specific form
  validateBankAccountNo(form: FormGroup) {
    const bankAccountNo = form.get('bank_account_number')?.value;
    const bankCode = form.get('bank_code')?.value;

    if (bankAccountNo?.length === 10 && form.get('bank_code')?.dirty) {
      this.commonService
        .validateBankAccountNo(bankAccountNo, bankCode)
        .subscribe({
          next: (response) => {
            form.get('bank_account_name')?.setValue(response.data.accountName);
            this.cdr.detectChanges();
          },
          error: () => {
            this.toast.error('Failed to validate bank account number');
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
